import styles from './LimitCard.module.css';
import Button from '../Button/Button';
import StatusBadge from '../StatusBadge/StatusBadge';

// A finite pool the admin controls: how much of it has gone, how much is left,
// and a button to re-aim it. Used for the two rationed giveaways — the signup
// welcome bonus (Points page) and the platform-wide coupon ceiling (Coupon
// page) — which look and behave the same but sit on different screens.
//
// `limit` of 0 means unlimited, matching the API. That is a real setting, not
// an unset one, so it renders as its own state rather than an empty bar.

const LimitCard = ({
    title,
    description,
    used = 0,
    limit = 0,
    usedNoun = 'used',
    status,
    statusVariant,
    actionLabel = 'Edit',
    onAction,
    footnote,
}) => {
    const unlimited = !limit || limit <= 0;
    const remaining = unlimited ? null : Math.max(0, limit - used);
    // Clamped: `used` can legitimately sit above `limit` after an admin lowers
    // the cap to close an offer early, and a bar past 100% would look broken.
    const percent = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
    const exhausted = !unlimited && remaining === 0;

    return (
        <div className={`${styles.card} p-4 h-100 d-flex flex-column`}>
            <div className="d-flex align-items-start justify-content-between gap-3">
                <div>
                    <h3 className={`${styles.title} mb-1`}>{title}</h3>
                    {description && <p className={`${styles.description} mb-0`}>{description}</p>}
                </div>
                {status && <StatusBadge status={status} variant={statusVariant} />}
            </div>

            <div className="mt-4">
                <div className="d-flex align-items-baseline gap-2">
                    <span className={styles.used}>{used.toLocaleString()}</span>
                    <span className={styles.limit}>
                        {unlimited ? usedNoun : `of ${limit.toLocaleString()} ${usedNoun}`}
                    </span>
                </div>

                {unlimited ? (
                    <p className={`${styles.remaining} mt-2 mb-0`}>No limit set — unlimited</p>
                ) : (
                    <>
                        <div className={`${styles.track} mt-3`}>
                            <div
                                className={`${styles.fill} ${exhausted ? styles.fillExhausted : ''}`}
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                        <p className={`${styles.remaining} mt-2 mb-0`}>
                            {exhausted
                                ? 'None left — the offer is closed'
                                : `${remaining.toLocaleString()} remaining`}
                        </p>
                    </>
                )}
            </div>

            {footnote && <p className={`${styles.footnote} mt-3 mb-0`}>{footnote}</p>}

            <Button variant="outline" className={`${styles.action} mt-4`} onClick={onAction}>
                {actionLabel}
            </Button>
        </div>
    );
};

export default LimitCard;
